import { formatFileSize, splitFileNameExtension } from '~/components/cara-input/cara-attachment';

/**
 * One document inside a combined PDF, in the position it will occupy. `kind`
 * says which slot of a render attachment it fills; `type` is the artifact type,
 * used to determine its rendering and signature options.
 */
export type AttachmentGroupMember = {
  id: string;
  kind: 'artifact' | 'file';
  name: string;
  type?: string;
};

/**
 * Several documents that travel as one PDF.
 *
 * The merge runs when the group is attached, so by the time a group exists
 * `fileId` names an ordinary workspace file holding the merged bytes — which is
 * what the draft stores, and all the draft can store. `members` is kept beside
 * it so the sender can reopen, reorder, rename, or dissolve the group, and is
 * lost when the composer is closed: nothing on the artifact records it.
 */
export type AttachmentGroup = {
  /** The merged PDF as stored; also the id that goes on the draft. */
  fileId: string;
  name: string;
  size: number;
  members: AttachmentGroupMember[];
  /** The member the other two sit before and after. Always one `canCarryRender` accepts. */
  anchorId: string;
  /** Kept with the recipe so editing the PDF preserves its producer signature. */
  producerInformationId?: string;
};

/**
 * Matches the render mutation's primary types. PDF_FILE can be concatenated as
 * a segment, but cannot be the document being rendered.
 */
const PRIMARY_ARTIFACT_TYPES = new Set([
  'DOCUMENT_HTML',
  'DOCUMENT',
  'TEMPLATE_FORM',
  'CUSTOM_FORM'
]);

/** Whether this member can be the artifact the merge renders around. */
export const canCarryRender = (member: AttachmentGroupMember): boolean =>
  member.kind === 'artifact' && PRIMARY_ARTIFACT_TYPES.has(member.type ?? '');

/**
 * Whether this member is already a PDF, so a merge only has to concatenate it.
 * Uploaded files reach the composer as stored PDFs, and a PDF_FILE artifact is
 * one the workspace happens to hold.
 */
export const isPdfMember = (member: AttachmentGroupMember): boolean =>
  member.kind === 'file' || member.type === 'PDF_FILE';

/**
 * Whether this sequence merges by plain concatenation. Every member being a PDF
 * means nothing has to be rendered, which is the one case that needs no anchor:
 * there is no document whose styling or producer signature could be lost.
 *
 * Fewer than two members is not a merge at all, so it is false rather than a
 * degenerate merge that returns its only input.
 */
export const isPdfOnlyGroup = (members: AttachmentGroupMember[]): boolean =>
  members.length >= MIN_GROUP_MEMBERS && members.every(isPdfMember);

/** Producer signatures are supported by template forms, including ACORD forms. */
export const supportsProducerSignature = (member: AttachmentGroupMember | undefined): boolean =>
  member?.kind === 'artifact' && member.type === 'TEMPLATE_FORM';

/**
 * Combining pages requires at least two documents. A single document is only
 * worth generating as a PDF when a supported producer signature customizes it.
 */
export const MIN_GROUP_MEMBERS = 2;

export const EMPTY_CUSTOMIZATION_MESSAGE =
  'No customization remains. Add pages or a signature, or remove this PDF and attach the original document instead.';

export type GroupRenderPlan = {
  primaryArtifactId: string;
  prefixAttachments: { fileId?: string; artifactId?: string }[];
  suffixAttachments: { fileId?: string; artifactId?: string }[];
};

const renderAttachment = (member: AttachmentGroupMember) =>
  member.kind === 'artifact' ? { artifactId: member.id } : { fileId: member.id };

/**
 * Splits a sequence into the shape the render takes: the anchor rendered in the
 * middle with PDFs concatenated on either side. The order the sender arranged
 * survives untouched; only the anchor's position in it decides the split.
 *
 * Undefined when `anchorId` names nothing in the sequence, or names something
 * that cannot be rendered — the one composition this route cannot merge.
 */
export const groupRenderPlan = (
  members: AttachmentGroupMember[],
  anchorId: string
): GroupRenderPlan | undefined => {
  const primaryIndex = members.findIndex((member) => member.id === anchorId);
  if (primaryIndex < 0 || !canCarryRender(members[primaryIndex])) {
    return undefined;
  }
  return {
    primaryArtifactId: members[primaryIndex].id,
    prefixAttachments: members.slice(0, primaryIndex).map(renderAttachment),
    suffixAttachments: members.slice(primaryIndex + 1).map(renderAttachment)
  };
};

/**
 * Why this sequence cannot be combined, or undefined when it can. Said while
 * the sender is still arranging it, because the alternative is a merge that
 * fails after the wait it takes to render every document in it.
 */
export const groupRefusal = (
  members: AttachmentGroupMember[],
  anchorId: string,
  producerInformationId?: string | null
): string | undefined => {
  const hasSignature =
    producerInformationId &&
    supportsProducerSignature(members.find((member) => member.id === anchorId));
  if (members.length < MIN_GROUP_MEMBERS && !hasSignature) {
    return EMPTY_CUSTOMIZATION_MESSAGE;
  }
  // A sequence of PDFs concatenates without rendering anything, so it needs no
  // anchor and the render plan below does not apply to it.
  if (isPdfOnlyGroup(members)) {
    return undefined;
  }
  if (!groupRenderPlan(members, anchorId)) {
    return 'A combined PDF has to include at least one workspace document. Attach these files on their own instead.';
  }
  return undefined;
};

/**
 * The members as merge attachments, in the order the sender arranged them. Used
 * only for a PDF-only merge, where every member is a segment and none is primary.
 */
export const groupMergeAttachments = (
  members: AttachmentGroupMember[]
): { fileId?: string; artifactId?: string }[] => members.map(renderAttachment);

/**
 * The filename the recipient will see, derived from what the group holds. Named
 * for its first document and the count, because a combined PDF that borrowed
 * only the first document's name would be indistinguishable from that document
 * sent alone.
 */
export const defaultGroupName = (members: AttachmentGroupMember[]): string => {
  const first = members[0];
  if (!first) {
    return 'Combined.pdf';
  }
  const { base } = splitFileNameExtension(first.name);
  const rest = members.length - 1;
  return rest > 0 ? `${base} and ${rest} more.pdf` : `${base}.pdf`;
};

/** Everything the group holds, in order, for the chip's hover text. */
export const groupContents = (members: AttachmentGroupMember[]): string =>
  members.map((member) => member.name).join(', ');

export const groupSummary = (group: AttachmentGroup): string =>
  `${group.members.length} document${group.members.length === 1 ? '' : 's'} · ${formatFileSize(group.size)}`;

/**
 * Forces the `.pdf` the merge actually produces onto whatever the sender typed.
 * A combined PDF named `packet.docx` would open in nothing.
 */
export const groupFileName = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) {
    return 'Combined.pdf';
  }
  return trimmed.toLowerCase().endsWith('.pdf') ? trimmed : `${trimmed}.pdf`;
};
